import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { Cron, CronType, SetValueFunctionExtra } from 'react-js-cron';
import 'react-js-cron/dist/styles.css';

import {
  AllowedDropdownPeriodsWithoutWeekDays,
  AllowedDropdownPeriodsWithWeekDays,
  EmrDataParamaters,
  presetValues,
  ALLOWED_TIME_PERIODS,
} from './Constants';
import {
  StyledTitle,
  StyledWrapper,
  StyledParameterTitle,
  ParametersWrapper,
  SchedullerHeader,
  ParametersContent,
} from './styled';
import { EmrFrequencySettingType } from 'src/routes/Customers/components/OperatorsTab/modules/types';
import Connector, { PropsFromRedux } from './Connector';
import ActionButton from 'src/components/general-ui/Buttons/ActionButton';
import { intl } from 'src/utils/errorHandling/messages';
import { ExtraActionBtn } from '../../style';
import BorderedButton from 'src/components/general-ui/Buttons/BorderedButton';
import LoadingOverlay from 'src/components/general-ui/LoadingOverlay';

type Props = PropsFromRedux & {
  setDisabledNextBtn: Dispatch<SetStateAction<boolean>>;
};

const EmrFrequencySettings = ({
  getEmrSchedullingFrequncyByOperatorId,
  emrFrequencySettingsData,
  createEmrSchedullingFrequncyByOperatorId,
  isEmrFrequencyLoading,
  setDisabledNextBtn,
}: Props): JSX.Element => {
  const [emrFrequencySetting, setFrequencySetting] =
    useState<EmrFrequencySettingType>({});
  const [allowedPeriodDropdowns, setAllowedPeriodDropdowns] = useState<{
    [x: string]: CronType[];
  }>({});

  useEffect(() => {
    getEmrSchedullingFrequncyByOperatorId();
  }, [getEmrSchedullingFrequncyByOperatorId]);

  useEffect(() => {
    if (emrFrequencySettingsData) {
      setFrequencySetting(emrFrequencySettingsData);
    }
  }, [emrFrequencySettingsData]);

  const handleChange =
    (emrParameter: string) => (value: string, extra: SetValueFunctionExtra) => {
      const { selectedPeriod } = extra;
      setDisabledNextBtn(true);

      if (
        selectedPeriod === ALLOWED_TIME_PERIODS.MONTH ||
        selectedPeriod === ALLOWED_TIME_PERIODS.DAY
      ) {
        setAllowedPeriodDropdowns(prevState => ({
          ...prevState,
          [emrParameter]: AllowedDropdownPeriodsWithoutWeekDays,
        }));
      } else if (selectedPeriod === ALLOWED_TIME_PERIODS.WEEK) {
        setAllowedPeriodDropdowns(prevState => ({
          ...prevState,
          [emrParameter]: AllowedDropdownPeriodsWithWeekDays,
        }));
      }
      setFrequencySetting(prevState => ({
        ...prevState,
        [emrParameter]: value,
      }));
    };

  return (
    <StyledWrapper>
      <SchedullerHeader>
        <StyledTitle>
          <FormattedMessage {...messages.emrFrequencyTitle} />
        </StyledTitle>
        <ActionButton
          onClick={() =>
            setFrequencySetting(prevState => ({
              ...prevState,
              ...presetValues,
            }))
          }
        >
          {intl.formatMessage(messages.presetSchedulling)}
        </ActionButton>
      </SchedullerHeader>
      <ParametersContent>
        {isEmrFrequencyLoading ? (
          <LoadingOverlay />
        ) : (
          EmrDataParamaters.map(emrParameter => (
            <ParametersWrapper key={emrParameter.label}>
              <StyledParameterTitle>{emrParameter.label}</StyledParameterTitle>
              {emrFrequencySetting &&
              emrFrequencySetting?.[
                emrParameter.value as keyof EmrFrequencySettingType
              ] ? (
                <Cron
                  value={
                    emrFrequencySetting?.[
                      emrParameter.value as keyof EmrFrequencySettingType
                    ] || ''
                  }
                  setValue={handleChange(emrParameter.value)}
                  humanizeValue={true}
                  clearButton={true}
                  clockFormat="12-hour-clock"
                  defaultPeriod={ALLOWED_TIME_PERIODS.WEEK}
                  leadingZero={['month-days']}
                  className="styled-cron-component"
                  mode={'multiple'}
                  allowedDropdowns={
                    allowedPeriodDropdowns[emrParameter.value] ||
                    AllowedDropdownPeriodsWithoutWeekDays
                  }
                  allowedPeriods={Object.values(ALLOWED_TIME_PERIODS)}
                  clearButtonAction="empty"
                  clearButtonProps={{
                    type: 'default',
                  }}
                />
              ) : (
                <BorderedButton
                  className="ant-btn-default"
                  onClick={() =>
                    setFrequencySetting(prevState => ({
                      ...prevState,
                      [emrParameter.value]: '0 0 * * *',
                    }))
                  }
                >
                  {intl.formatMessage(messages.schedule)}
                </BorderedButton>
              )}
            </ParametersWrapper>
          ))
        )}
      </ParametersContent>
      <ExtraActionBtn
        disabled={isEmrFrequencyLoading}
        onClick={() =>
          createEmrSchedullingFrequncyByOperatorId(emrFrequencySetting)
        }
      >
        {intl.formatMessage(messages.saveSchedulling)}
      </ExtraActionBtn>
    </StyledWrapper>
  );
};

const messages = defineMessages({
  emrFrequencyTitle: {
    id: 'EmrFrequencySettings.index.emrFrequencyTitle',
    defaultMessage: 'Select EMR Update frequency',
  },
  every: {
    id: 'EmrFrequencySettings.index.every',
    defaultMessage: 'Every',
  },
  on: {
    id: 'EmrFrequencySettings.index.on',
    defaultMessage: 'on',
  },
  at: {
    id: 'EmrFrequencySettings.index.at',
    defaultMessage: 'at',
  },
  saveSchedulling: {
    id: 'EmrFrequencySettings.index.saveSchedulling',
    defaultMessage: 'Save schedulling',
  },
  presetSchedulling: {
    id: 'EmrFrequencySettings.index.presetSchedulling',
    defaultMessage: 'Preset values',
  },
  schedule: {
    id: 'EmrFrequencySettings.index.schedule',
    defaultMessage: 'Schedule CRON job',
  },
});

export default Connector(EmrFrequencySettings);
